import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18next from 'i18next'
import { patientService } from '@/services/api'
import { queryKeys } from '@/config/queryKeys'
import type { GetPatientsParams } from '@/services/api/patients/patient.static'

export const usePatients = (params?: GetPatientsParams) => {
  return useQuery({
    queryKey: queryKeys.patients.list({ ...params }),
    queryFn: () => patientService.getPatients(params),
  })
}

export const usePatient = (id: string) => {
  return useQuery({
    queryKey: queryKeys.patients.detail(id),
    queryFn: () => patientService.getPatient(id),
    enabled: !!id,
  })
}

export const useCreatePatient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: patientService.createPatient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.lists() })
      toast.success(i18next.t('patients:patientCreated'))
    },
    onError: () => {
      toast.error(i18next.t('patients:errors.createFailed'))
    },
  })
}

export const useUpdatePatient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof patientService.updatePatient>[1]
    }) => patientService.updatePatient(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.detail(id) })
      toast.success(i18next.t('patients:patientUpdated'))
    },
    onError: () => {
      toast.error(i18next.t('patients:errors.updateFailed'))
    },
  })
}

export const useDischargePatient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof patientService.dischargePatient>[1]
    }) => patientService.dischargePatient(id, data),
    onSuccess: (_data, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.detail(id) })
      const key =
        data.status === 'DECEASED' ? 'patients:patientDeceased' : 'patients:patientDischarged'
      toast.success(i18next.t(key))
    },
    onError: () => {
      toast.error(i18next.t('common:errors.saveFailed'))
    },
  })
}

export const useReactivatePatient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => patientService.reactivatePatient(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.detail(id) })
      toast.success(i18next.t('patients:patientReactivated'))
    },
    onError: () => {
      toast.error(i18next.t('patients:errors.reactivateFailed'))
    },
  })
}

export const useDeletePatient = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => patientService.deletePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.lists() })
      toast.success(i18next.t('patients:patientDeleted'))
    },
    onError: () => {
      toast.error(i18next.t('patients:errors.deleteFailed'))
    },
  })
}
